import { FamilyAnswersResponseDto } from '@families-link/shared';
import { Box, Typography } from '@mui/material';
import { createColumnHelper, ColumnDef } from '@tanstack/react-table';
import { format } from 'path';
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

const helper = createColumnHelper<FamilyAnswersResponseDto>();

const ExpandableCell = ({ text }: { text: string }) => {
	const [expanded, setExpanded] = useState(false);

	const toggle = () => setExpanded((prev: any) => !prev);

	return (
		<Box sx={{ py: 1 }}>
			<Typography
				sx={{
					fontSize: '14px',
					lineHeight: '120%',
					...(expanded
						? {
								display: 'flex',
								height: '100%',

								whiteSpace: 'normal',
								wordBreak: 'break-word',
								overflowWrap: 'break-word',
						  }
						: {
								display: '-webkit-box',
								WebkitLineClamp: 1,
								WebkitBoxOrient: 'vertical',
								overflow: 'hidden',
								textOverflow: 'ellipsis',
						  }),
				}}
			>
				{text}
			</Typography>

			{text.length > 100 && (
				<Typography
					component="span"
					onClick={toggle}
					sx={{
						mt: 0.5,
						cursor: 'pointer',
						color: 'var(--secondary-action-button, #3C8DBC)',
						fontSize: '12px',
						fontWeight: 400,
						textDecoration: 'underline',
						fontFamily: 'Rubik',
						lineHeight: '120%',
					}}
				>
					{expanded ? 'Collapse' : 'View more'}
				</Typography>
			)}
		</Box>
	);
};

export function useFamilyQuestionAnswersTableColumns(): ColumnDef<FamilyAnswersResponseDto, string>[] {
	const [t] = useTranslation();
	const location = useLocation();
	const isAdminRoute = location.pathname.startsWith('/admin');

	const result = useMemo(
		() => [
			helper.accessor('questionText', {
				header: t('surveys.question-answers.columns.question'),
				meta: {
					flex: 1,
				},
				cell: ({ getValue }) => getValue() || '-',
			}) as ColumnDef<FamilyAnswersResponseDto, string>,

			helper.accessor('questionType', {
				header: t('surveys.question-answers.columns.question-type'),
				meta: {
					flex: 1,
				},
				cell: ({ getValue }) => getValue() || '-',
			}) as ColumnDef<FamilyAnswersResponseDto, string>,
			helper.accessor('isRequired', {
				header: t('surveys.question-answers.columns.is-required'),
				meta: {
					flex: 1,
				},
				cell: ({ getValue }) => {
					const isRequired = getValue<boolean>();
					return isRequired ? 'Required' : 'Optional';
				},
			}) as ColumnDef<FamilyAnswersResponseDto, string>,

			helper.accessor('answer', {
				header: t('surveys.question-answers.columns.answer'),
				meta: {
					flex: 2,
					heightAuto: true,
					sx: {
						whiteSpace: 'normal',
						overflow: 'visible',
					},
				},
				cell: ({ getValue }) => {
					const value = getValue();
					const displayValue = Array.isArray(value) ? value.join(', ') : value;

					if (!displayValue) {
						return (
							<Typography
								sx={{
									color: 'var(--Grey, #9397A4)',
									fontSize: '12px',
									fontStyle: 'normal',
									fontWeight: 400,
								}}
							>
								No answer
							</Typography>
						);
					}

					return <ExpandableCell text={displayValue} />;
				},
			}) as ColumnDef<FamilyAnswersResponseDto, string>,
		],
		[t]
	);

	return result;
}
